import { useEffect, useState } from 'react';
import {
  FULL_CONNECTOME_MANIFESTS,
  type FullConnectomeManifest,
  type FullConnectomeSex,
} from './fullConnectome';
import {
  FULL_BRAIN_PROTOCOL_VERSION,
  type FullBrainWorkerResponse,
} from './fullBrainProtocol';

interface FullBrainProps {
  readonly sex: FullConnectomeSex;
}

type FullBrainStatus =
  | { readonly state: 'loading'; readonly manifest: FullConnectomeManifest }
  | { readonly state: 'ready'; readonly manifest: FullConnectomeManifest; readonly webGpuAvailable: boolean; readonly message: string }
  | { readonly state: 'error'; readonly manifest: FullConnectomeManifest; readonly message: string };

function formatCount(value: number): string {
  return new Intl.NumberFormat('en-US').format(value);
}

function formatBytes(value: number | undefined): string {
  if (value === undefined) return 'official portal';
  return `${(value / 1_000_000_000).toFixed(1)} GB`;
}

export function FullBrain({ sex }: FullBrainProps) {
  const [status, setStatus] = useState<FullBrainStatus>({
    state: 'loading',
    manifest: FULL_CONNECTOME_MANIFESTS[sex],
  });

  useEffect(() => {
    const manifest = FULL_CONNECTOME_MANIFESTS[sex];
    const requestId = `${manifest.id}-${Date.now()}`;
    const worker = new Worker(new URL('./fullBrainWorker.ts', import.meta.url), { type: 'module' });

    setStatus({ state: 'loading', manifest });
    worker.onmessage = (event: MessageEvent<FullBrainWorkerResponse>) => {
      const response = event.data;
      if (response.type === 'ready' && response.requestId === requestId) {
        setStatus({
          state: 'ready',
          manifest: response.manifest,
          webGpuAvailable: response.webGpuAvailable,
          message: response.message,
        });
      } else if (response.type === 'error' && response.requestId === requestId) {
        setStatus({ state: 'error', manifest, message: response.message });
      }
    };
    worker.postMessage({
      type: 'boot',
      protocolVersion: FULL_BRAIN_PROTOCOL_VERSION,
      requestId,
      sex,
    });

    return () => {
      worker.postMessage({ type: 'dispose', protocolVersion: FULL_BRAIN_PROTOCOL_VERSION });
      worker.terminate();
    };
  }, [sex]);

  const { manifest } = status;
  const statusLabel = status.state === 'loading'
    ? 'loading manifest'
    : status.state === 'error'
      ? 'worker error'
      : 'metadata only · data not bundled';

  return (
    <article className="full-brain-card">
      <div className="full-brain-heading">
        <div>
          <span className="eyebrow">FULL CONNECTOME / {manifest.id}</span>
          <h3>{manifest.sex === 'male' ? '♂' : '♀'} {manifest.dataset}</h3>
        </div>
        <span className={`status-pill status-${status.state}`}>{statusLabel}</span>
      </div>

      <div className="full-brain-metrics">
        <div><strong>{formatCount(manifest.neurons)}</strong><span>neurons in that dataset</span></div>
        <div><strong>{formatCount(manifest.connections)}</strong><span>connections in that dataset</span></div>
        <div><strong>{formatBytes(manifest.graphBytes)}</strong><span>held at the source, not here</span></div>
      </div>

      {status.state === 'error' ? (
        <p className="full-brain-error">{status.message}</p>
      ) : (
        <p className="full-brain-message">
          {status.state === 'loading' ? 'Worker validates the versioned dataset contract…' : status.message}
        </p>
      )}

      {status.state === 'ready' && (
        <p className="full-brain-capability">
          {status.webGpuAvailable
            ? '⚡ WebGPU detected — this device could host a future local projection of the source graph. No such projection ships today.'
            : 'WebGPU not detected — the official viewer remains available in every supported desktop browser.'}
        </p>
      )}

      <div className="full-brain-actions">
        <a className="primary action-link" href={manifest.viewerUrl} target="_blank" rel="noreferrer">
          Open official whole-brain viewer ↗
        </a>
        <a className="secondary action-link" href={manifest.sourceUrl} target="_blank" rel="noreferrer">
          Provenance ↗
        </a>
        <a className="secondary action-link" href={manifest.downloadUrl} target="_blank" rel="noreferrer">
          Data portal ↗
        </a>
      </div>

      <p className="full-brain-disclaimer">
        The numbers above are real public dataset metadata (neuron and connection counts, licence, dataset id) read from a manifest in
        this app. <strong>No connectome data is bundled here</strong> — the source graphs stay on the official Janelia and FlyWire
        infrastructure and are only ever reached by leaving this page. This is not a claim that a wiring diagram alone is a complete
        simulated mind. The compact 9-region LIF demo remains available locally, and its inter-region weights are hand-invented for
        illustration rather than measured.
      </p>
    </article>
  );
}
